import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, Download, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { userManagementService, UserProfile, UserManagementStats, NationalityDistribution, GenderDistribution } from '../../services/userManagementService';
import { qrCodeManagementService, QRCodeStats } from '../../services/qrCodeManagementService';
import UserKPICards from './components/UserKPICards';
import DemographicsPanel from './components/DemographicsPanel';
import UserFilters from './components/UserFilters';
import { Helmet } from 'react-helmet';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [stats, setStats] = useState<UserManagementStats | null>(null);
  const [qrStats, setQrStats] = useState<QRCodeStats | null>(null);
  const [nationalityData, setNationalityData] = useState<NationalityDistribution[]>([]);
  const [genderData, setGenderData] = useState<GenderDistribution[]>([]);
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNationality, setSelectedNationality] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');
  const [ageRange, setAgeRange] = useState({ min: 0, max: 100 });

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all data including QR code stats
      const [allUsersData, statsData, qrStatsData, nationalityDist, genderDist] = await Promise.all([
        userManagementService.getAllUsersWithStats(),
        userManagementService.getUserManagementStats(),
        qrCodeManagementService.getQRCodeStats(),
        userManagementService.getNationalityDistribution(),
        userManagementService.getGenderDistribution()
      ]);

      setAllUsers(allUsersData);
      setStats(statsData);
      setQrStats(qrStatsData);
      setNationalityData(nationalityDist);
      setGenderData(genderDist);
    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load user data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on current filters for demographics preview
  const getFilteredUsers = () => {
    return allUsers.filter(user => {
      // Search filter
      if (searchTerm && !user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) && 
          !user.email.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      // Nationality filter
      if (selectedNationality !== 'all' && user.nationality !== selectedNationality) {
        return false;
      }
      
      // Gender filter
      if (selectedGender !== 'all' && user.gender !== selectedGender) {
        return false;
      }
      
      // Age filter
      if (user.age !== null && (user.age < ageRange.min || user.age > ageRange.max)) {
        return false;
      }
      
      return true;
    });
  };

  const filteredUsers = getFilteredUsers();

  const handleExportUsers = async () => {
    try {
      const csvContent = [
        ['Name', 'Email', 'Age', 'Nationality', 'Gender', 'Total Savings', 'Avg Redemption', 'Conversion Rate'],
        ...filteredUsers.map(user => [
          user.fullName,
          user.email,
          user.age || 'N/A',
          user.nationality || 'N/A',
          user.gender || 'N/A',
          `${user.totalSavings.toFixed(2)}`,
          `${user.avgRedemptionAmount.toFixed(2)}`,
          `${user.conversionRate.toFixed(1)}%`
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-management-filtered-data.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting users:', error);
    }
  };

  // Render content based on state
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading user management data...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 text-xl mb-4">⚠️ Error</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={loadUserData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">User Management Dashboard</h1>
              <p className="mt-2 text-gray-600">
                Comprehensive user analytics, demographics, and engagement insights for TravelHeroes platform 
                <br />
                <span className="text-sm font-medium text-blue-600">
                  ✓ Real-time analytics • {allUsers.length} total users • Advanced filtering available
                </span>
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link
                to="/user-analytics-table"
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                User Analytics Table
              </Link>
              
              <button
                onClick={handleExportUsers}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="h-4 w-4 mr-2" />
                Export Filtered CSV
              </button>
              
              <button
                onClick={loadUserData}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Refresh Data
              </button>
            </div>
          </div>
        </div>

        {/* KPI Cards with Activation Stats */}
        {stats && qrStats && (
          <UserKPICards
            totalUsers={stats.totalUsers}
            avgAge={stats.avgAge}
            avgSavingsPerUser={stats.avgSavingsPerUser}
            totalSavings={stats.totalSavings}
            avgConversionRate={stats.avgConversionRate}
            filteredCount={filteredUsers.length}
            activatedUsers={qrStats.activatedUsers}
            activationRate={qrStats.activationRate}
          />
        )}

        {/* Filters Section */}
        <UserFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          selectedNationality={selectedNationality}
          onNationalityChange={setSelectedNationality}
          selectedGender={selectedGender}
          onGenderChange={setSelectedGender}
          ageRange={ageRange}
          onAgeRangeChange={setAgeRange}
          nationalityOptions={nationalityData.map(n => n.nationality)}
          genderOptions={genderData.map(g => g.gender)}
        />

        {/* Main Content Grid - Demographics Focus */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Demographics Panel - Full Width */}
          <div className="lg:col-span-2">
            <DemographicsPanel
              demographics={{
                nationalityData,
                genderData
              }}
              nationalityData={nationalityData}
              genderData={genderData}
              topSavers={filteredUsers
                .sort((a, b) => b.totalSavings - a.totalSavings)
                .slice(0, 10)
                .map(user => ({
                  name: user.fullName,
                  savings: user.totalSavings,
                  nationality: user.nationality || 'Unknown'
                }))
              }
            />
          </div>

          {/* Quick Stats Panel */}
          <div className="space-y-6">
            {/* Filtered Users Overview */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Filter Overview</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Matching Users</span>
                  <span className="text-lg font-bold text-blue-600">{filteredUsers.length}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Savings</span>
                  <span className="text-lg font-bold text-green-600">
                    €{filteredUsers.reduce((sum, u) => sum + u.totalSavings, 0).toFixed(0)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Active Users</span>
                  <span className="text-lg font-bold text-purple-600">
                    {filteredUsers.filter(u => u.conversionRate > 0).length}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Avg Age</span>
                  <span className="text-lg font-bold text-orange-600">
                    {filteredUsers.length > 0 ? 
                      (filteredUsers.reduce((sum, u) => sum + (u.age || 0), 0) / filteredUsers.filter(u => u.age !== null).length).toFixed(1) : 
                      '0'
                    }
                  </span>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <Link
                  to="/user-analytics-table"
                  className="w-full flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View Complete Analytics Table
                </Link>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              
              <div className="space-y-3">
                <button className="w-full flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-left">
                  <Users className="h-4 w-4 mr-2" />
                  Bulk User Operations
                </button>
                
                <button className="w-full flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-left">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Generate User Report
                </button>
                
                <button className="w-full flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-left">
                  <Download className="h-4 w-4 mr-2" />
                  Advanced Export
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Dashboard Summary</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{allUsers.length}</div>
              <div className="text-sm text-gray-600">Total Users</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                €{allUsers.reduce((sum, u) => sum + u.totalSavings, 0).toFixed(0)}
              </div>
              <div className="text-sm text-gray-600">Platform Total Savings</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {allUsers.filter(u => u.conversionRate > 0).length}
              </div>
              <div className="text-sm text-gray-600">Converted Users</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {filteredUsers.length}
              </div>
              <div className="text-sm text-gray-600">Filtered Results</div>
            </div>
          </div>

          {/* Data Sync Information */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Last updated: {new Date().toLocaleString()} • Real-time data synchronization
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-green-600">Live Data</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>User Management - TravelHeroes Admin Dashboard</title>
        <meta name="description" content="Comprehensive user management and administration dashboard for TravelHeroes platform" />
      </Helmet>

      <div className="p-6">
        {renderContent()}
      </div>
    </>
  );
};

export default UserManagement;